/* SYSTEMS: the world clock and the reactions of `matter.ts`, run against the WORLD.
 *
 * ONE MAP, NOT A SCREEN AT A TIME (LS-511, 2026-09-29). Everything here once ran on the screen underfoot: its fires, its
 * fields, its tide, and ledgers of what burned or was cut filed per screen and replayed on walking back in. Tiles are
 * WORLD tiles now (0 to WORLD_W * COLS across), the fields are one field over the whole world, every fire burns wherever
 * it is, and the clocks run in a WINDOW round the body -- the chunks under it and beside it -- which is the only thing
 * about a screen this file still knows. The Game calls `update` once a frame and `ignite` when the ember, a lit brazier
 * or a blast touches something that can burn.
 *
 * Determinism: every roll is seeded by (world, tick, tile) in `matter.ts`; the clock is the run's frame count. */
import { T, TS, UI } from './art.ts';
import { COLS, ROWS, WORLD_W, WORLD_H } from './world.ts';
import { mix } from './rng.ts';
/** The night's pack (LS-379): how often one arrives, how many at once before the tier, and the grace at the start of a run. */
export const PROWL_EVERY = 420, PROWL_CAP = 2, PROWL_GRACE = 3 * 60 * 60;
/** THE NIGHT GROWS WITH THE DISTANCE FROM THE GLADE (round 53's design lens). A still body on the glade lost no
 *  vigor all night and the same body two screens out died twice inside a night-minute: a cliff at the glade's
 *  edge. The first ring gets one hunter, the next the country's two, and only beyond that the country's tier. */
export function prowlCap(game, tier = 0) {
  const d = gladeDistance(game);
  return d <= 2 ? 1 : d <= 4 ? PROWL_CAP : PROWL_CAP + Math.min(2, tier);
}
/** HOW FAR FROM THE GLADE, AS THE CROW FLIES, on the home island (2026-09-13; Vijay: "too many monsters initially").
 *  The road's length was the measure, and on the island world the road winds: two screens from the glade read ten
 *  or twelve and the night brought the country's whole tier. Off the home island the road's length still counts. */
export function gladeDistance(game) {
  const w = game.world; if (!w || !w.start) return 9;
  const W0 = w.biome[0].length, isle = (x: number, y: number) => w.island ? w.island[y * W0 + x] : 0;
  if (isle(game.area.x, game.area.y) === isle(w.start.x, w.start.y)) return Math.max(Math.abs(game.area.x - w.start.x), Math.abs(game.area.y - w.start.y));
  return w.dist ? (w.dist.get(game.area.x + ',' + game.area.y) ?? 9) : 9;
}
/** The night's pack as a thing in the world (LS-380): its size when the world
 *  is made, the most it grows to, how far it smells prey, and how far from
 *  the glade its den is. */
export const PACK_SIZE = 3, PACK_MAX = 6, PACK_RANGE = 3, DEN_AWAY = 3;
/** A den burned out: this many scorched tiles on the den's screen when the pack comes home at dawn, and it moves on, halved (LS-380's den, 2026-09-11). */
export const DEN_BURNT = 12;
/** Frames a Great Beast must be held in the deep before it drowns: twenty seconds, against three for the small kin (LS-379). */
export const BEAST_DROWN = 1200;
import { Flame, TORCH_FRAMES, Enemy, ENEMY_DEFS } from './entities.ts';
import { RNG } from './rng.ts';
import { MARK_CAP } from './game.ts';   // the beasts' cap on marks a screen, shared by the pack's (LS-380); used at call time only, so the cycle is harmless
import { FRAMES_PER_MINUTE, matterOf, traitsOf, clockOf, tickFire, regrow, tideKind, urge, BIOME_DRY, rainAt, moodOfSeed } from './matter.ts';
import { Fields, DEPTH_FORD } from './sim/fields.ts';
import type { Rect } from './sim/fields.ts';
import { afflict, afflicted, tickAfflictions, cure } from './sim/afflictions.ts';
import { BIOME_WARMTH, FROST_LINE, SCORCH_LINE, DEPTH_DEEP, DEPTH_SHALLOW, DEEP_LINE, WET_LINE } from './sim/fields.ts';
import { wearing, wieldedWeapon, forgeRecipes, matchingForgeRecipe, addItem } from './sim/inventory.ts';
import { cutTurf } from './grass.ts';
import { tickFireThings, SimWorld, igniteTile, bodyThings, standInFire, setBrazierKind, setTidalKinds, setPushKind, setThornKind, setNestKind, emit, thing, Fears, DrawnToLight, EatsFire, urgeOf } from './sim/things.ts';
setBrazierKind(T.BRAZIER); setTidalKinds(T.WATER, T.SHALLOW); setPushKind(T.PUSH); setThornKind(T.THORN); setNestKind(T.NEST);
import { walkableTile, reachableTile, isWet } from './passable.ts';
import type { Fire, Grid } from './matter.ts';

export const FIRE_TICK = 6;         // frames between fire ticks
export const FIRE_CAP = 256;        // the most tiles alight in the world at once
export const TIDE_TICK = 240;       // frames between the tide's moves
export const REGROW_MINUTES = 8;    // game-minutes before scorch is ground again
export const TUFT_MINUTES = 6;      // game-minutes before cut grass is moss again (LS-176 step 4)
export const BURN_EVERY = 36;       // frames between hurts to a body standing in fire
export const EBB_FRAMES = 60 * 60;   // frames the called ebb holds
export const EBB_REACH = 16;         // tiles round where it was called that the ebb holds for: one basin, not the world
export const LIT_FRAMES = 600;     // frames a player carries fire after standing in it
export const RIPEN_MINUTES = 2;     // game-minutes between pods ripening, below a pouch of five
export const NEST_SPREAD_MINUTES = 3;   // game-minutes before a wild nest seeds another (LS-176 step 6)
export const NEST_CAP = 3;              // nests within NEST_REACH tiles of one another
export const NEST_REACH = 8;

/** WHETHER FILLING A TILE SEALS A WAY (LS-176 step 6; LS-511). It flooded the SCREEN and asked that the count of
 *  its passable parts not change, so a hedge was judged by where the chunk grid happened to fall. Now it asks the
 *  ground itself: the open tiles beside (tx, ty) must still reach one another round it, within SEAL_REACH tiles,
 *  with the tile filled -- bare-handed and with every charm, so neither a walker nor a charm's route is cut. A way
 *  round longer than that counts as sealed, which errs toward refusing. `grid` is any grid, the world's included. */
export const SEAL_REACH = 10;
const ALL_CHARMS = { pods: true, ember: true, tide: true };
export function sealsNothing(grid: Grid, tx: number, ty: number): boolean {
  const was = grid.at(tx, ty);
  if (was === undefined) return false;
  const joined = (pass: (v: number) => boolean) => {
    const sides = [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => [tx + dx, ty + dy]).filter(([x, y]) => { const v = grid.at(x, y); return v !== undefined && pass(v); });
    if (sides.length < 2) return true;
    const seen = new Set<string>([sides[0][0] + ',' + sides[0][1], tx + ',' + ty]); const q = [sides[0]];
    while (q.length) {
      const [cx, cy] = q.pop() as number[];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = cx + dx, ny = cy + dy, k = nx + ',' + ny;
        if (seen.has(k) || Math.abs(nx - tx) > SEAL_REACH || Math.abs(ny - ty) > SEAL_REACH) continue;
        const v = grid.at(nx, ny); if (v === undefined || !pass(v)) continue;
        seen.add(k); q.push([nx, ny]);
      }
    }
    return sides.every(([x, y]) => seen.has(x + ',' + y));
  };
  return joined((v) => walkableTile(v)) && joined((v) => reachableTile(v, ALL_CHARMS));
}

export class Systems {
  fires: Fire[] = [];                                         // every tile alight in the world, in world tiles
  /* THE LEDGERS, one per kind of change, keyed by world tile 'x,y' (LS-511: they were filed per screen and replayed on
   * walking back in). Each is re-laid on a chunk when it is first made (`applyToChunk`), and its clock runs in the
   * window round the body. */
  scorched: Map<string, number> = new Map();   // 'x,y' -> minute it burned
  tufts: Map<string, number> = new Map();      // 'x,y' -> minute the grass was cut (LS-176 step 4)
  nests: Map<string, number> = new Map();      // 'x,y' -> minute a wild nest was planted or last spread (LS-176 step 6)
  marks: Map<string, number> = new Map();      // 'x,y' -> the tile kind a push or a set-down left there (LS-176 step 2)
  wind: [number, number];
  ticks = 0;
  baseWet: Map<string, number> = new Map();                  // 'x,y' -> the tile's kind before the tide moved it
  dryBed: Map<string, number> = new Map();                   // 'x,y' -> what a drained basin leaves (LS-241)
  wetSeen: Set<string> = new Set();                          // chunks whose water has been read into baseWet
  lastTide = 0;
  tideCall: { until: number; tide: number; x: number; y: number } | null = null;   // where the ebb was called: one basin, as the docs say, not the whole world
  lastRipen: number | undefined = undefined;                 // the minute the pouch last gained a pod

  /** THE FIELDS OF THE WHOLE WORLD (LS-223; one field, not one a screen, LS-511). */
  field: Fields = new Fields(WORLD_W * COLS, WORLD_H * ROWS);
  pack: any = null;   // the night's pack, wherever it is (LS-380)
  packMinute = -1;   // fires left burning on screens you are not on (LS-379)
  rainOverride: number | null = null;
  constructor(seedNum: number) {
    const a = ((mix(seedNum, 3, 9) & 1023) / 1024) * Math.PI * 2;   // one wind a world at dawn...
    this.windBase = a; this.wind = [Math.cos(a), Math.sin(a)];
  }

  clock(game) { return clockOf(game.seedNum, game.runFrames ?? game.frame ?? 0); }

  /** The world as the reactions see it: world tiles, read and written through the game. */
  grid(game): Grid {
    return {
      cols: WORLD_W * COLS, rows: WORLD_H * ROWS,
      at: (x, y) => game.tileAt(x, y),
      set: (x, y, v) => { game.setTileAt(x, y, v); },
    };
  }
  /** THE WINDOW the clocks run in: the chunk under the body and the eight round it, in world tiles. */
  window(game): Rect {
    const ax = game.area ? game.area.x : 0, ay = game.area ? game.area.y : 0;
    return { x0: Math.max(0, (ax - 1) * COLS), y0: Math.max(0, (ay - 1) * ROWS), x1: Math.min(WORLD_W * COLS, (ax + 2) * COLS), y1: Math.min(WORLD_H * ROWS, (ay + 2) * ROWS) };
  }
  /** Has the tide read every chunk of the window yet? */
  windowSeen(game) { const a = game.area; if (!a) return true; for (let sy = Math.max(0, a.y - 1); sy <= Math.min(WORLD_H - 1, a.y + 1); sy++) for (let sx = Math.max(0, a.x - 1); sx <= Math.min(WORLD_W - 1, a.x + 1); sx++) if (!this.wetSeen.has(sx + ',' + sy)) return false; return true; }
  /** The world tile a body stands on. */
  tileOf(b): [number, number] { return [Math.floor(b.cx / TS), Math.floor(b.cy / TS)]; }

  biomeDry(game) {
    const b = game.world ? game.world.biome[game.area.y][game.area.x] : 'heath';
    return Math.min(1.4, (BIOME_DRY[b] ?? 0.6) * moodOfSeed(game.seedNum).dry);   // a dry world's grass takes fire faster (2026-09-27)
  }

  /** THE FORGE (LS-384): every rock beside a lit brazier is read for what lies on it; the first recipe that
   *  whole set covers is made, its offerings gone into the fire and the product laid beside the rock. */
  forge(game) {
    const rocks = new Map<string, any[]>();
    for (const it of game.items) if (game.offeredAtForge(it, it.forgeTx, it.forgeTy) && game.isForgeRock(it.forgeTx, it.forgeTy)) {
      const k = it.forgeTx + ',' + it.forgeTy; if (!rocks.has(k)) rocks.set(k, []); rocks.get(k)!.push(it);
    }
    for (const [k, lying] of rocks) {
      const [tx, ty] = k.split(',').map(Number), cx = tx * TS + 8, cy = ty * TS + 4;
      const have: Record<string, number> = {}; for (const it of lying) have[it.kind] = (have[it.kind] || 0) + 1;
      /* THE TELL IS GRADED (2026-09-12): one spark for one thing lying, a steadier crackle as the pile nears a recipe --
       * the nearest recipe's share of its needs met -- and the flare when it lands; the same teaching the storm uses */
      let nearest = 0;
      for (const r of forgeRecipes()) { let met = 0, need = 0; for (const [kind, n] of Object.entries(r.needs)) { need += n; met += Math.min(have[kind] || 0, n); } nearest = Math.max(nearest, met / need); }
      game.puff(cx, cy, UI.flame, 1 + Math.round(nearest * 3), 'ember');   // the tell: a rock with an offering on it sparks, more as the pile grows whole
      // Switching materials is not a deadline. Keep the pile intact while the
      // player is at the work rock, including when turning or opening the bag.
      const p = game.player;
      const working = p && Math.hypot(p.cx - cx, p.cy - (ty * TS + 8)) <= TS * 1.5;
      /* ...BUT NOT FOR EVER (LS-459, 2026-09-26). A finished pile burned only once the body was more than a tile and a
       * half off, and nothing on the glass says to step away: the newcomer probe laid the raft's last cutting, stood,
       * and took it back. Standing at the rock, the fire waits five seconds after the last piece -- time to choose the
       * next one (a flint then a sinew is a knife, not a shard) -- and then burns; stepping away still burns it at once. */
      const settle = working ? 300 : 45;
      if (lying.some((it: any) => game.frame - (it.laidAt ?? -999) < settle)) continue;
      const recipe = matchingForgeRecipe(have);
      if (!recipe) continue;
      for (const [kind, n] of Object.entries(recipe.needs)) { let left = n; for (const it of lying) if (left > 0 && it.kind === kind && !it.dead) { it.dead = true; left--; } }
      game.dropAt(recipe.out, tx * TS + 2, ty * TS + 18, { dropped: true });   // beside the rock, where a body can stand -- and marked as a thing lying in the world, or the save did not write it and a reload ate what the fire had just made (2026-09-17)
      game.puff(cx, cy, UI.flame, 14, 'ember');
      game.sfx('flame', cx);
      game.state.forged = (game.state.forged || 0) + 1;
    }
  }
  burning(x: number, y: number) { return this.fires.some((f) => f.x === x && f.y === y); }
  simWorld(game) { const w = new SimWorld(this.grid(game), game.seedNum); w.tick = this.ticks; return w; }
  /* WEAR AND WEATHER ON THE BODIES (LS-224). Every frame the ground under a foot wears a little (a walker a
   * fiftieth, a creature half that); where the wear passes seven tenths the turf is cut, so a path forms where
   * anyone keeps walking. A body out in the rain is Soaked for four seconds after: slower, and its torch will
   * not stay lit. The afflictions tick here too. */
  wearAndWeather(game) {
    const f = this.field, win = this.window(game);
    const wear = f.field('wear', 0.9995);
    const tread = (b, amount) => {
      const [tx, ty] = this.tileOf(b);
      if (!f.inside(tx, ty)) return;
      f.bump('wear', tx, ty, amount);
      /* the grass is the cosmetic layer of the chunk underfoot, cut where a path has worn */
      if (wear.v[f.idx(tx, ty)] >= 0.7 && game.turfAt) { const tf = game.turfAt(tx, ty); if (tf && !(tf.cell && tf.cell.cut > 0)) { cutTurf(tf.turf, COLS, ROWS, tf.lx * TS + 8, tf.ly * TS + 8, 5, null); if (tf.here && game.refreshTurfArt) game.refreshTurfArt(false); } }   // on the half-second beat: a trampling beast wore a path every frame, and the picture is a full bake
    };
    const p = game.player;
    if (p && !p.submerged && !p.swimming) tread(p, 0.02);
    for (const e of game.enemies || []) if (!e.dead) tread(e, 0.01);
    /* MORE FIELDS (LS-224; Vijay: "we need more afflictions than just heat
     * wetness growth"). SMOKE rises from every burning tile and drifts a tile
     * downwind; SPORES settle where a spore lands and seep from a nest;
     * SCENT is where the walker has been, for whatever hunts by it. Each
     * decays on its own clock, and standing in one is an affliction. */
    f.field('smoke', 0.985); f.field('spores', 0.992); f.field('scent', 0.997);
    const wx = Math.sign(this.wind ? this.wind[0] : 0), wy = Math.sign(this.wind ? this.wind[1] : 0);
    for (const fire of this.fires || []) { f.bump('smoke', fire.x, fire.y, 0.08); if (wx || wy) f.bump('smoke', fire.x + wx, fire.y + wy, 0.04); }
    if (this.ticks % 4 === 0) for (const [k] of this.nests) { const [nx, ny] = k.split(',').map(Number); if (nx >= win.x0 && ny >= win.y0 && nx < win.x1 && ny < win.y1 && game.tileAt(nx, ny) === T.NEST) f.bump('spores', nx, ny, 0.03); }
    // smoke spreads: a tile keeps four fifths of its smoke and gives a twentieth to each neighbour, so the air beside a fire is thick and the air two tiles off is thin
    if (this.ticks % 2 === 0) { const sm = f.field('smoke').v, W = f.cols, rw = win.x1 - win.x0, src = new Float32Array(rw * (win.y1 - win.y0));
      for (let ty = win.y0; ty < win.y1; ty++) for (let tx = win.x0; tx < win.x1; tx++) src[(ty - win.y0) * rw + tx - win.x0] = sm[ty * W + tx];
      const at = (x: number, y: number) => x < win.x0 || y < win.y0 || x >= win.x1 || y >= win.y1 ? 0 : src[(y - win.y0) * rw + x - win.x0];
      for (let ty = win.y0; ty < win.y1; ty++) for (let tx = win.x0; tx < win.x1; tx++) {
        const n = at(tx - 1, ty) + at(tx + 1, ty) + at(tx, ty - 1) + at(tx, ty + 1);
        sm[ty * W + tx] = Math.min(1, at(tx, ty) * 0.8 + n * 0.05);
      } }
    /* THE TRAIL (LS-527): a body on land lays scent; in the water it lays none, and rain washes what is laid away */
    if (this.ticks % 10 === 0 && this.rain(game) > 0.3) { const sc = f.field('scent').v, W = f.cols; for (let ty = win.y0; ty < win.y1; ty++) for (let tx = win.x0; tx < win.x1; tx++) sc[ty * W + tx] *= 0.97; }
    if (p && !p.submerged) { const [tx, ty] = this.tileOf(p); if (!p.wading && !p.swimming) f.bump('scent', tx, ty, 0.05);
      const mantle = wearing(game.state, 'mantle'), hide = wearing(game.state, 'hide');   // the bramble mantle and the sow hide (LS-239)
      // ...and the plate names the piece that kept it off (LS-298)
      if (f.at('smoke', tx, ty) >= 0.25) { if (!mantle) afflict(p, 'Choking', 120); else game.gearSaved('mantle'); }
      if (f.at('spores', tx, ty) >= 0.4) { if (!mantle) afflict(p, 'Spored', 240); else game.gearSaved('mantle'); }
      if ((p.wading || p.swimming) && this.clock(game).night) { if (!hide && !wearing(game.state, 'frost') && !((game.state.warm || 0) > game.runFrames)) afflict(p, 'Chilled', 240); else game.gearSaved(hide ? 'hide' : 'frost'); }   // cold water in the dark; the frost charm answers it too, and a salt cake for a while (LS-335)
    }
    const rain = this.rain(game);
    if (p && rain > 0.3 && !p.submerged) { if (!wearing(game.state, 'oilskin') && !((game.state.dry || 0) > game.runFrames)) afflict(p, 'Soaked', 240); else game.gearSaved('oilskin'); }   // ...or a bitterleaf, for a while   // the oilskin (LS-335): rain runs off it -- and a soaking is 15% of your pace and the end of your torch
    for (const e of game.enemies || []) if (!e.dead && rain > 0.3) afflict(e, 'Soaked', 240);
    /* THE CREATURES ARE THINGS IN THE SAME WORLD (LS-242; Vijay chose all three Qud-style directions). What the fields
     * do to the player they do to everything alive in them, by what the creature is made of: a body scorches over the
     * scorch line and chills under the frost line, smoke chokes what breathes, spores take what is not already spores
     * or bramble, and a body that cannot float drowns in deep water. `traitsOf` is the table that says which. */
    for (const e of game.enemies || []) {
      if (e.dead || e.def.ghost || e.spawnFx > 0) continue;
      const tr = traitsOf(e.type || e.kind);
      const [tx, ty] = this.tileOf(e);
      if (!f.inside(tx, ty)) continue;
      const bodily = tr.burns !== false;   // a wisp is a light, not a body: nothing of the weather touches it
      if (bodily) {
        const tmp = f.tempAt(tx, ty);
        if (tmp > SCORCH_LINE) afflict(e, 'Scorched', 120);
        if (tmp < FROST_LINE) afflict(e, 'Chilled', 240);
        const plant = (e.type || e.kind) === 'bramble' || (e.type || e.kind) === 'bramblemother';
        if (!plant && f.at('smoke', tx, ty) >= 0.25) afflict(e, 'Choking', 120);
        const spory = plant || (e.type || e.kind) === 'sporeling';
        if (!spory && f.at('spores', tx, ty) >= 0.4) afflict(e, 'Spored', 240);
      }
      /* AND WHAT CANNOT FLOAT GOES UNDER (LS-242). Three seconds of deep water and a body that does not float drowns:
       * a shellback is stone, a charger is a boar, and luring either into the deep is a way to fight it. A Great Beast
       * goes under too, slowly (LS-379, slice four): twenty seconds held in the deep is a plan, not a cheese. */
      if (!tr.floats && !(e.def.boss && !e.def.beast) && game.isSwimAt && game.isSwimAt(e.cx, e.cy)) {   // the three Great Beasts carry `boss` as well as `beast`; the guard is for a boss that is not one
        e.sink = (e.sink || 0) + 1;
        if (e.sink % 20 === 0 && game.puff) game.puff(e.cx, e.cy, UI.paper, 3, 'spark');
        if (e.sink >= (e.def.beast ? BEAST_DROWN : 180)) { e.dead = true; if (game.sfx) game.sfx('splash', e.x + 8); if (game.puff) game.puff(e.cx, e.cy, UI.paper, 10); if (game.onEnemyKilled) game.onEnemyKilled(e); }
      } else e.sink = 0;
    }
    if (p) tickAfflictions(game, p);
    for (const e of game.enemies || []) tickAfflictions(game, e);
    f.tickMore(win);
    /* THE TEMPERATURE (LS-240): each tile's country's base, the sun's height, the night, the rain and the fires make the
     * target; the field settles toward it. A body standing under the frost line chills (a hide or a lit torch keeps
     * it off); over the scorch line it scorches (the bramble mantle keeps that off); water cools a scorched body.
     * THE SUN'S HEIGHT OVER THE DAYLIGHT HALF (2026-09-18): nought at dawn, one at noon, nought at dusk. */
    { const clock = this.clock(game); const warm = clock.day < 0.5 ? Math.sin(clock.day * Math.PI * 2) : 0;
      /* A LIT BRAZIER IS A FIRE TO THE TEMPERATURE (LS-379): a hearth, not a wildfire -- it warms and does not scorch */
      const warmth: { x: number; y: number; hearth?: boolean }[] = (this.fires || []).map((q: any) => ({ x: q.x, y: q.y }));
      for (const l of this.braziers(game, win)) warmth.push({ x: l.x, y: l.y, hearth: true });
      f.tempBase = this.tempBaseFor(game);
      f.tickTemp(f.tempBase, warm, clock.night, this.rain(game), warmth, win);
      f.tickedAt = game.runFrames;
      if (p && !p.submerged) {
        const [tx, ty] = this.tileOf(p); const tmp = f.tempAt(tx, ty);
        /* nothing chills on the hull (2026-09-19), as the docs say */
        if (tmp < FROST_LINE && !(p.lit > 0) && !game.aboard) { if (!wearing(game.state, 'hide') && !wearing(game.state, 'frost') && !((game.state.warm || 0) > game.runFrames)) afflict(p, 'Chilled', 240); else game.gearSaved(wearing(game.state, 'hide') ? 'hide' : 'frost'); }
        /* ...OR HAS DRUNK ONE (LS-335, fourth batch): the ash draught is twenty seconds of the mantle's answer to burning ground */
        const draughted = (game.state.draught || 0) > game.runFrames;
        if (tmp > SCORCH_LINE && !draughted && !p.wading && !p.swimming) { if (wearing(game.state, 'mantle')) game.gearSaved('mantle'); else if (wearing(game.state, 'soles')) game.gearSaved('soles'); else afflict(p, 'Scorched', 120); }   // ash soles (LS-335): the ground burns what stands on it, and they stand between
        if ((p.wading || p.swimming) && afflicted(p, 'Scorched')) cure(p, 'Scorched');
      } }
  }
  /** Each tile's base warmth: its chunk's country, and the world's mood. */
  tempBaseFor(game): (x: number, y: number) => number {
    const b = game.world && game.world.biome, warm = moodOfSeed(game.seedNum).warm;
    return (x: number, y: number) => { const row = b && b[Math.floor(y / ROWS)]; const biome = row ? row[Math.floor(x / COLS)] : 'heath'; return (BIOME_WARMTH[biome] ?? 0.5) + warm; };
  }
  /** Lit braziers in a window of world tiles, read off the chunks' tiles. */
  braziers(game, win: Rect): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    for (let sy = Math.floor(win.y0 / ROWS); sy * ROWS < win.y1; sy++) for (let sx = Math.floor(win.x0 / COLS); sx * COLS < win.x1; sx++) {
      if (sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) continue;
      const t = game.screenAt(sx, sy).tiles;
      for (let ly = 0; ly < ROWS; ly++) for (let lx = 0; lx < COLS; lx++) if (t[ly][lx] === T.BRAZIER_LIT) { const x = sx * COLS + lx, y = sy * ROWS + ly; if (x >= win.x0 && y >= win.y0 && x < win.x1 && y < win.y1) out.push({ x, y }); }
    }
    return out;
  }
  /** How hard it rains now: the weather clock's answer, or what a check asked for. */
  rain(game): number {
    if (this.rainOverride !== null && this.rainOverride !== undefined) return this.rainOverride;
    if (!game.area) return 0;   // no rain underground
    return rainAt(game.seedNum, this.clock(game).minutes);
  }
  /** THE NIGHT COMES FOR YOU (LS-379, the need).
   *
   *  Nothing in the game required anything: glim bought vigor and that was
   *  the whole economy, so every system was a toy. This is the first need. At
   *  night, every seven seconds, one of the country's own creatures arrives
   *  at the edge of the glass and hunts -- nine turns in ten toward the
   *  player, never idling -- up to a cap that grows with the screen's tier.
   *  At dawn they turn away and walk off, and the glass takes them.
   *
   *  THE ANSWER IS ALREADY IN THE WORLD. A creature that fears fire will not
   *  come within three tiles and a half of a light at night (matter.ts,
   *  urge), so a lit brazier or a torch in hand is the first shelter, and a
   *  ring of thorn is a wall to them already. A lit brazier also warms the
   *  ground it stands on now, so a fire in the fen is the cold's answer too.
   *
   *  THREE MINUTES OF GRACE. The clock's phase is the seed's, so a run can
   *  begin at night; the pack does not come in the first three minutes,
   *  which is the first day of a run that started at dusk. */
  /** WHAT THE HOUR NO LONGER ADMITS LEAVES (2026-09-19). The roster is filtered by the hour, the rain and the tide
   *  when a screen is filled and never again, so a tidecrab stayed at the flood, a stormgull after the rain and a
   *  day kind all night, for as long as the screen was kept. On the glass they turn away and walk off the way the
   *  dawn's hunters do, and the glass takes them; one that cannot leave in ten seconds is taken where it stands. */
  hourPasses(game) {
    if ((globalThis as any).__noProwl) return;
    if (!game.tiles || game.mode !== 'play' || game.paused || !game.rosterKinds) return;
    const gated = (game.enemies || []).filter((e: any) => e && !e.dead && !e.def.boss && !e.prowler && (e.def.lowTide || e.def.rain || e.def.day || e.def.night || e.def.dusk));
    if (!gated.length) return;
    const admitted = new Set(game.rosterKinds(Array.from(new Set<string>(gated.map((e: any) => e.type)))));
    for (const e of gated) {
      if (admitted.has(e.type)) { if (e.hourOut) { e.fleeing = false; e.hourOut = 0; } continue; }   // its hour came back before it left: it stays
      if (!e.hourOut) { e.fleeing = true; e.timer = 0; }
      e.hourOut = (e.hourOut || 0) + 30;
      if (!game.inWindow(e, TS * 2) || e.hourOut > 600) { e.dead = true; if (e.hourOut > 600) game.puff(e.cx, e.cy, UI.linen, 4, 'dust'); }
    }
  }
  prowl(game) {
    if ((globalThis as any).__noProwl) return;
    if (!game.tiles || game.mode !== 'play' || game.paused) return;
    const night = this.clock(game).night;
    const prowlers = (game.enemies || []).filter((e: any) => e && !e.dead && e.prowler);
    if (!night) {
      /* DAWN. On the glass they flee; off it they are gone, and they leave
       * nothing, because a body the dawn takes was never yours to loot. */
      for (const e of prowlers) {
        if (!e.fleeing) { e.fleeing = true; e.timer = 0; }
        /* ...AND ONE THAT CANNOT LEAVE IS TAKEN ANYWAY (2026-09-19). A fleeing hunter faces away from the player and
         * walks; against a wall, a hedge or the sea it stood there, alive, for the rest of the run -- a night's
         * creature on the glass at noon, striking whoever walked into it. Ten seconds is more than any of them needs
         * to cross half a screen; one still on the glass after that is stuck, and the dawn takes it where it stands. */
        e.fled = (e.fled || 0) + 1;
        if (!game.inWindow(e, TS * 2) || e.fled > 600) { e.dead = true; if (e.fled > 600) game.puff(e.cx, e.cy, UI.linen, 4, 'dust'); }
      }
      return;
    }
    /* AND NOTHING PROWLS INTO A LIVING BEAST'S LAIR (2026-09-13). Measured with the expedition's own tally: fighting the
     * sow at full vigor the body took 21 points from her and NINETEEN from the lesser creatures standing on her lair --
     * a bramble for 14, a sporeling, a shellback -- so half the fight was the crowd. The roster on a lair screen was
     * already cut to one (game.fillArea); the night's prowl walked more in behind it. The lair is the beast's. */
    if (game.beastLairs && (game.beastLairs() || []).some((l: any) => l.x === game.area.x && l.y === game.area.y && !(game.state.beastsSlain || []).includes(l.kind))) return;
    if (game.runFrames < PROWL_GRACE) return;
    if (this.ticks % PROWL_EVERY !== 0) return;
    const tier = game.world && game.world.tier ? game.world.tier[game.area.y][game.area.x] : 0;
    /* THE PACK, WHEN IT IS HERE, IS WHAT COMES (LS-380). Its size is the cap
     * and it comes in by the side it arrived from; the country's own stray
     * hunters keep the old rule on every other night. */
    const pack = this.packHere(game);
    const cap = pack ? (gladeDistance(game) <= 2 ? Math.min(pack.size, prowlCap(game, tier)) : pack.size) : prowlCap(game, tier);   // the pack hunts the glade's ring one at a time (2026-09-13)
    if (prowlers.length >= cap) return;
    /* FROM THE EDGE OF THE GLASS, on ground that will take it. Twelve tries
     * at a random edge tile just outside the window; a screen with no such
     * ground -- an islet, a cliff top -- gets nobody tonight. */
    /* NOT THE WISPS. A wisp is drawn to light, and it already comes to your
     * fire at night on its own (LS-176); a wisp that HUNTED would walk into
     * the one shelter the night has, which is the rule cancelling itself. The
     * pack is what fears fire. */
    const pool = (game.prowlPool ? game.prowlPool() : ['sporeling']).filter((k: string) => !traitsOf(k).drawnToLight);
    if (!pool.length) return;
    const cam = game.camera();
    const rng = new RNG(mix(game.seedNum, this.ticks, 4711));
    const kindRng = new RNG(mix(game.seedNum, this.ticks, 4712));   // the KIND is drawn from its own stream (2026-09-18): choosing it before the tile is judged -- which an aquatic prowler needs -- would otherwise shift every side and place the night arrives from
    /* FROM THE EDGE OF THE GLASS FIRST, AND FROM THE FAR SIDE OF THE SCREEN
     * IF THE EDGES ARE ROCK (2026-09-11). Twelve tries at the edge was the
     * whole rule, and on a dust screen ringed with cliff every one of them
     * failed, every seven seconds, all night: a player standing still there
     * for forty-five seconds was never visited. The night has to come for
     * you wherever you are, so the last tries look inside the screen, at any
     * ground at least six tiles from the body. */
    for (let tries = 0; tries < 20; tries++) {
      const inside = tries >= 12;
      // came in from the west (moved east): the west edge, and so round
      const side = pack && !inside && (pack.from[0] || pack.from[1]) ? (pack.from[0] === 1 ? 0 : pack.from[0] === -1 ? 1 : pack.from[1] === 1 ? 2 : 3) : rng.int(4);
      const px = inside ? cam.x + (1 + rng.int(COLS - 2)) * TS + 8 : side === 0 ? cam.x - TS : side === 1 ? cam.x + COLS * TS + 2 : cam.x + rng.int(COLS * TS);   // inside: anywhere on the glass (it was the screen underfoot)
      const py = inside ? cam.y + (1 + rng.int(ROWS - 2)) * TS + 8 : side === 2 ? cam.y - TS : side === 3 ? cam.y + ROWS * TS + 2 : cam.y + rng.int(ROWS * TS);
      if (inside && Math.hypot(px - game.player.cx, py - game.player.cy) < 6 * TS) continue;
      const sx = Math.floor(px / (COLS * TS)), sy = Math.floor(py / (ROWS * TS));
      if (!game.world || sx < 0 || sy < 0 || !game.world.biome[sy] || game.world.biome[sy][sx] === undefined) continue;
      const k = game.tileAtPx ? game.tileAtPx(px, py) : undefined;
      /* THE TILE IS JUDGED FIRST, AND THE KIND CHOSEN TO SUIT IT (2026-09-18). An eel's own collision makes dry land
       * a wall, so every eel the night sent stood frozen and alive where it landed for the rest of the run -- and
       * refusing water outright, as this did, is why it could only ever land on land. A land tile takes the kinds
       * that walk; a deep-water tile takes the kinds that swim; a tile that suits neither is passed over, as before. */
      if (k === undefined) continue;
      const wet = k === T.WATER;
      if (!wet && (!walkableTile(k) || k === T.SHALLOW)) continue;
      const usable = pool.filter((q: string) => !!(ENEMY_DEFS[q] && (ENEMY_DEFS[q] as any).aquatic) === wet);
      if (!usable.length) continue;
      const type = kindRng.pick(usable);
      const e: any = new Enemy(type, px, py, new RNG(mix(game.seedNum, this.ticks, tries, 99)), { hpBonus: tier + 1 });
      /* THE WHOLE BODY, NOT ITS CORNER (2026-09-30): the tile was judged at the box's top-left alone, so a sporeling
       * landed with its right half in a cliff (night-5: corners 9,31,9,31) and stood frozen until the dawn's deadline. */
      if (game.solid && [[0, 0], [e.w - 1, 0], [0, e.h - 1], [e.w - 1, e.h - 1]].some(([ox, oy]) => game.solid(e.x + ox, e.y + oy, false, e))) continue;
      e.prowler = true; e.spawnFx = 0; e.home = game.area.x + ',' + game.area.y; e.came = inside ? -1 : side; e.ofPack = !!pack;
      e.timer = 0;   // its first turn is now, and it is toward the player
      game.enemies.push(e);
      if (game.sfx) game.sfx('growl', e.x + 8);
      break;
    }
  }

  /** The world's fields (one for the whole map, LS-511). */
  fieldsFor(_game?): Fields { return this.field; }

  /** Start a fire on a tile if it can burn. Returns true when it caught. */
  ignite(game, tx: number, ty: number, force = false): boolean {
    const grid = this.grid(game);
    if (grid.at(tx, ty) === undefined || this.burning(tx, ty) || this.fires.length >= FIRE_CAP) return false;
    /* THROUGH THE THINGS (LS-222): the tile's parts answer 'ignite'. A wet tile refuses; a brazier lights and stays
     * lit; anything burnable rolls unless forced. World tiles (LS-511): a creature burning a screen away lights its own
     * ground, not the same numbers under the player's feet. */
    const w = new SimWorld(grid, game.seedNum); w.tick = this.ticks;
    w.fires = this.fires.map((f) => ({ id: 0, kind: -1, x: f.x, y: f.y, parts: new Map() } as any));
    const before = w.fires.length;
    const lit = igniteTile(w, tx, ty, force, game);
    if (!lit) return false;
    for (let i = before; i < w.fires.length; i++) {
      const f = w.fires[i], fp = f.parts.get('Fire');
      this.fires.push({ x: f.x, y: f.y, fuel: fp.fuel, born: this.ticks, kind: fp.kind });
      this.flameFor(game, f.x, f.y);
    }
    return true;
  }
  /** The picture of a burning tile: one Flame for each fire, made when the fire is. */
  flameFor(game, x: number, y: number) {
    game.flames = game.flames || [];
    if (!game.flames.some((q) => !q.dead && q.tx === x && q.ty === y)) game.flames.push(new Flame(x, y));
  }

  flare(game, tx: number, ty: number, fuel: number, struck = false) {
    if (this.burning(tx, ty) || this.fires.length >= FIRE_CAP) return;
    this.fires.push({ x: tx, y: ty, fuel, born: this.ticks, struck } as any);
    this.flameFor(game, tx, ty);
  }


  /** HEAVY THINGS ARE PUSHED (LS-176 step 2): what a boulder left behind and where it now stands are written down,
   *  by world tile, and put back when their chunk is made (`applyToChunk`). */
  mark(_game, x: number, y: number, kind: number) { this.marks.set(x + ',' + y, kind); }
  /** A break is the last word on a tile: whatever the ledgers said stood there, they say nothing now. */
  unmark(x: number, y: number) { const k = x + ',' + y; this.marks.delete(k); this.nests.delete(k); this.tufts.delete(k); }

  /* THE STORM (LS-381; Vijay: "we need more elements than just fire water and air, eg lightning"). A downpour
   * charges the sky; when the charge is full the sky flickers for half a second and then a strike comes down
   * on the tallest thing under it -- a lightning rod first, then a body carrying a spear or wearing the plate,
   * then a tree, a standing stone, a cairn or a brazier, and failing all of those a random tile. What it hits
   * burns even in the rain (the one fire the rain cannot start any other way), a brazier lights, a struck body
   * takes two, a rod stuns whatever stands beside it, and the jolt goes through the water: everything standing
   * in the wet in view is stunned, the player wading takes one. The rod is the tool: set down, it takes
   * every strike in view, so a storm becomes a thing you can aim. */
  charge = 0; flicker = 0; strikes = 0; lastStrike: any = null;
  lastWetQ = 0;   // the wet step the current screen was last painted at
  storm(game) {
    const rain = this.rain(game);
    if (rain < 0.6) { this.charge = Math.max(0, this.charge - 0.002); this.flicker = 0; return; }
    if (this.flicker > 0) { if (--this.flicker === 0) { this.charge = 0; this.strike(game); } return; }
    this.charge += rain * 0.0035;   // a downpour fills the sky in five to eight seconds
    if (this.charge >= 1) { this.flicker = 30; game.stormFlash = 2; game.sfx('rumble'); }   // the tell: the sky flickers a beat before, and a far rumble rolls
  }
  strike(game) {
    const rng = new RNG(mix(game.seedNum, this.ticks, 8811));
    const p = game.player;
    /* ON THE GLASS (LS-511): the tallest thing in sight takes it, not the tallest on the screen underfoot */
    const cam = game.camera ? game.camera() : { x: game.originX(), y: game.originY() };
    const win: Rect = { x0: Math.max(0, Math.floor(cam.x / TS)), y0: Math.max(0, Math.floor(cam.y / TS)), x1: Math.min(WORLD_W * COLS, Math.ceil((cam.x + COLS * TS) / TS)), y1: Math.min(WORLD_H * ROWS, Math.ceil((cam.y + ROWS * TS) / TS)) };
    let best: [number, number] | null = null, rank = 0;
    const consider = (tx: number, ty: number, r: number) => { if (r > rank || (r === rank && rng.chance(0.5))) { rank = r; best = [tx, ty]; } };
    for (let ty = win.y0; ty < win.y1; ty++) for (let tx = win.x0; tx < win.x1; tx++) {
      const t = game.tileAt(tx, ty);
      if (t === T.ROD) consider(tx, ty, 4);
      else if (t === T.TREE || t === T.STONE || t === T.CAIRN || t === T.BRAZIER || t === T.BRAZIER_LIT) consider(tx, ty, 2);
    }
    const armed = !!p && !p.submerged && (wieldedWeapon(game.state).kind === 'spear' || wearing(game.state, 'plate'));
    if (armed) { const [ptx, pty] = this.tileOf(p); consider(ptx, pty, 3); }
    for (let i = 0; i < 20 && !best; i++) { const tx = win.x0 + 1 + rng.int(Math.max(1, win.x1 - win.x0 - 2)), ty = win.y0 + 1 + rng.int(Math.max(1, win.y1 - win.y0 - 2)); if (game.tileAt(tx, ty) !== undefined) { best = [tx, ty]; rank = 1; } }
    if (!best) return;
    const [tx, ty] = best as [number, number];
    this.strikes++; this.lastStrike = { x: tx, y: ty, tick: this.ticks, rank };
    const sx = tx * TS + 8, sy = ty * TS + 8;
    game.stormFlash = 5; if (game.shakeFor) game.shakeFor(5, 10); game.sfx('thunder', sx);
    for (let i = 0; i < 14; i++) game.puff(sx, sy, UI.gold, 5, 'spark');
    const t = game.tileAt(tx, ty);
    if ((t === T.BRAZIER) && game.lightBrazierAt) game.lightBrazierAt(tx, ty, true);
    else if (t !== T.ROD && t !== T.WATER && t !== T.SHALLOW && t !== T.SEA && t !== T.ICE && t !== T.STONE && t !== T.CAIRN && t !== T.ROCK && t !== T.BRAZIER_LIT) {   // ...NOR ICE, WHICH IS WATER (2026-09-18)
      if (this.ignite(game, tx, ty, true)) { const f: any = this.fires.find((q) => q.x === tx && q.y === ty); if (f) f.struck = true; }
      else this.flare(game, tx, ty, 90, true);
    }
    /* ...AND A STRIKE SHATTERS ICE (the two elements meet): the struck tile and its four neighbours, if frozen, are
     * water again at once, and whatever stood on them is in it. */
    for (const [dx, dy] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]]) { const x = tx + dx, y = ty + dy; if (game.tileAt(x, y) === T.ICE) { const base = this.baseWet.get(x + ',' + y); game.setTileAt(x, y, base === T.SHALLOW ? T.SHALLOW : T.WATER); } }
    if (rank === 3 && p) p.hurt(game, 2, sx, sy - 8, 'lightning');
    for (const e of game.enemies || []) {
      if (e.dead) continue;
      const et = game.tileKindAt(e.cx, e.cy), near = Math.hypot(e.cx - sx, e.cy - sy) < 3.5 * TS;
      if (isWet(et) || (rank === 4 && near)) e.stun = Math.max(e.stun || 0, 60);   // the jolt goes through the water
    }
    if (p && rank !== 3 && (p.wading || p.swimming)) p.hurt(game, 1, sx, sy - 8, 'lightning');
  }

  /** A rock on a fire puts it out. */
  smother(game, tx: number, ty: number): boolean {
    const n = this.fires.length;
    this.fires = this.fires.filter((f) => f.x !== tx || f.y !== ty);
    if (game.flames) game.flames = game.flames.filter((f) => f.tx !== tx || f.ty !== ty);
    return this.fires.length < n;
  }

  /** ECOLOGY ON THE CLOCK (LS-176 step 6): a wild nest seeds another every few game-minutes, on a sheltered dry tile
   *  near it -- beside a tree or a thorn, no water next to it -- up to NEST_CAP within NEST_REACH tiles of one another,
   *  unless it was burnt or cut. Run in the window round the body (LS-511: it was replayed on entering a screen). A nest
   *  is solid, so it is planted only where it seals nothing. */
  nestSweep(game) {
    const now = this.clock(game).minutes, win = this.window(game), grid = this.grid(game);
    const inWin = (x: number, y: number) => x >= win.x0 && y >= win.y0 && x < win.x1 && y < win.y1;
    for (const [k, minute] of [...this.nests]) {
      const [x, y] = k.split(',').map(Number);
      if (!inWin(x, y)) continue;
      if (grid.at(x, y) !== T.NEST) { this.nests.delete(k); continue; }   // cut, burnt or trodden: it spreads nothing
      if (now - minute < NEST_SPREAD_MINUTES) continue;
      this.nests.set(k, now);
      let near = 0; for (const [k2] of this.nests) { const [x2, y2] = k2.split(',').map(Number); if (Math.abs(x2 - x) <= NEST_REACH && Math.abs(y2 - y) <= NEST_REACH) near++; }
      if (near >= NEST_CAP) continue;
      const spot = this.nestSpot(game, grid, x, y);
      if (!spot) continue;
      grid.set(spot[0], spot[1], T.NEST);
      this.nests.set(spot[0] + ',' + spot[1], now);
    }
  }

  /** A sheltered, dry, open tile within three of (nx, ny) that a nest can stand on without sealing anything; seeded by
   *  the nest, so a world spreads the same way twice. */
  nestSpot(game, grid: Grid, nx: number, ny: number): [number, number] | null {
    const spots: [number, number][] = [];
    for (let y = ny - 3; y <= ny + 3; y++) for (let x = nx - 3; x <= nx + 3; x++) {
      if (Math.max(Math.abs(x - nx), Math.abs(y - ny)) < 2) continue;   // not on top of its parent
      const v = grid.at(x, y);
      if (v !== T.GROUND && v !== T.MOSS && v !== T.TUFT) continue;
      let shelter = false, wet = false, nestNear = false;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
        const n = grid.at(x + dx, y + dy);
        if (n === T.TREE || n === T.THORN) shelter = true;
        if (n === T.NEST) nestNear = true;
        if (n !== undefined && matterOf(n).wet && Math.abs(dx) + Math.abs(dy) === 1) wet = true;
      }
      if (!shelter || wet || nestNear) continue;
      spots.push([x, y]);
    }
    if (!spots.length) return null;
    const start = (mix(game.seedNum, nx * 31 + ny, this.nests.size + 3) >>> 4) % spots.length;
    for (let i = 0; i < spots.length; i++) {
      const [x, y] = spots[(start + i) % spots.length];
      if (sealsNothing(grid, x, y)) return [x, y];
    }
    return null;
  }

  /** THE SICKLE HARVESTS (LS-176 step 4): grass the blade took down lies as
   *  a tuft. Only soil that carried grass (moss, ground) becomes one, never a
   *  locked tile and never one that is burning. Returns true when it did. */
  layTuft(game, tx: number, ty: number): boolean {
    const grid = this.grid(game);
    const v = grid.at(tx, ty);
    if (v !== T.MOSS && v !== T.GROUND) return false;
    if (this.burning(tx, ty)) return false;
    grid.set(tx, ty, T.TUFT);
    this.tufts.set(tx + ',' + ty, this.clock(game).minutes);
    return true;
  }

  /** Cut grass is moss again once the clock has run, and scorch grows back (LS-511: both were replayed on entering a
   *  screen; they are simply retired when their time comes, wherever they are). */
  regrowSweep(game) {
    const now = this.clock(game).minutes, grid = this.grid(game);
    for (const [k, minute] of [...this.tufts]) {
      if (now - minute < TUFT_MINUTES) continue;
      const [x, y] = k.split(',').map(Number);
      if (grid.at(x, y) === T.TUFT) grid.set(x, y, matterOf(T.TUFT).regrows ?? T.MOSS);
      this.tufts.delete(k);
    }
    for (const [k, minute] of [...this.scorched]) {
      if (now - minute < REGROW_MINUTES) continue;
      const [x, y] = k.split(',').map(Number);
      // THROUGH THE THINGS (LS-222): the scorched tile's Regrows part answers the clock
      const w = new SimWorld(grid, game.seedNum); w.tick = this.ticks; w.fields = this.field;
      w.fields.setGrowth(x, y, now - minute);   // THE GROWTH FIELD (LS-223): the minutes it has stood, for the part to read
      const t = w.tileAt(x, y);
      if (t && t.kind === T.SCORCH) emit(w, 'regrow', t, { minutes: w.fields.growthAt(x, y), after: REGROW_MINUTES });
      this.scorched.delete(k);
    }
  }
  /** WHAT THE LEDGERS SAY STANDS ON A CHUNK, laid on it when it is first made (after a reload, or on first sight):
   *  scorch the clock has not grown back, cut grass, the marks a push or a set-down left, and the nests. Anything the
   *  ledger holds that is already past its time is left for `regrowSweep` to retire. */
  applyToChunk(game, sx: number, sy: number, tiles: number[][]) {
    const ox = sx * COLS, oy = sy * ROWS, now = this.clock(game).minutes;
    const each = (m: Map<string, number>, fn: (lx: number, ly: number, v: number) => void) => { for (const [k, v] of m) { const [x, y] = k.split(',').map(Number); const lx = x - ox, ly = y - oy; if (lx >= 0 && ly >= 0 && lx < COLS && ly < ROWS) fn(lx, ly, v); } };
    each(this.scorched, (lx, ly, minute) => { const v = tiles[ly][lx]; if (now - minute < REGROW_MINUTES && v !== undefined && matterOf(v).ash === T.SCORCH) tiles[ly][lx] = T.SCORCH; });
    each(this.tufts, (lx, ly, minute) => { const v = tiles[ly][lx]; if (now - minute < TUFT_MINUTES && (v === T.MOSS || v === T.GROUND)) tiles[ly][lx] = T.TUFT; });
    each(this.marks, (lx, ly, kind) => { tiles[ly][lx] = kind; });
    each(this.nests, (lx, ly) => { const v = tiles[ly][lx]; if (v === T.GROUND || v === T.MOSS || v === T.TUFT) tiles[ly][lx] = T.NEST; });
    // ...and the ledger learns the nests the generator put here
    for (let ly = 0; ly < ROWS; ly++) for (let lx = 0; lx < COLS; lx++) if (tiles[ly][lx] === T.NEST && !this.nests.has((ox + lx) + ',' + (oy + ly))) this.nests.set((ox + lx) + ',' + (oy + ly), now);
  }


  /* THE THICKETS CREEP (LS-525, after Caves of Qud: plants that spread). Once a game-minute every chunk in reach may put
   * out one tile of thorn from a thicket it already has, into meadow or bare ground beside it -- one chance in two, never
   * within three tiles of the body, never onto a tile whose loss would part two pieces of open ground (sealsNothing: the
   * world stays ungated), and never past eight more than the chunk began with, or thirty. Fire, a blade and the sow
   * are what clear it; the growth is in the placed layer, so a reload keeps it. */
  thornBase: Map<string, number> = new Map();
  thornSpread(game) {
    if (!game.area || !game.player || game.mode !== 'play' || (globalThis as any).__noThornSpread) return;
    const grid = this.grid(game), [px, py] = this.tileOf(game.player);
    for (let sy = game.area.y - 1; sy <= game.area.y + 1; sy++) for (let sx = game.area.x - 1; sx <= game.area.x + 1; sx++) {
      if (sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) continue;
      const k = sx + ',' + sy, ox = sx * COLS, oy = sy * ROWS, thorns: [number, number][] = [];
      for (let y = oy; y < oy + ROWS; y++) for (let x = ox; x < ox + COLS; x++) if (grid.at(x, y) === T.THORN) thorns.push([x, y]);
      if (!this.thornBase.has(k)) this.thornBase.set(k, thorns.length);
      if (!thorns.length || thorns.length >= Math.min(30, this.thornBase.get(k)! + 8)) continue;
      const rng = new RNG(mix(game.seedNum, sx * 131 + sy, Math.floor(this.ticks / FRAMES_PER_MINUTE), 5250));
      if (!rng.chance(0.5)) continue;
      const [tx, ty] = thorns[rng.int(thorns.length)], [dx, dy] = rng.pick([[1, 0], [-1, 0], [0, 1], [0, -1]]) as [number, number];
      const nx = tx + dx, ny = ty + dy, v = grid.at(nx, ny);
      if (v !== T.MOSS && v !== T.GROUND && v !== T.TUFT) continue;
      if (Math.max(Math.abs(nx - px), Math.abs(ny - py)) <= 3 || this.burning(nx, ny)) continue;
      if ((game.enemies || []).some((e: any) => !e.dead && Math.floor(e.cx / TS) === nx && Math.floor(e.cy / TS) === ny)) continue;
      if (!sealsNothing(grid, nx, ny)) continue;
      game.setTileAt(nx, ny, T.THORN); if (game.markPlacedAt) game.markPlacedAt(nx, ny, T.THORN);
      game.puff(nx * TS + 8, ny * TS + 8, UI.leaf, 3, 'leaf');
    }
  }

  /** THE TIDE IS CALLED (LS-176). With the charm held, on dry land, the
   *  ebb comes to this screen for a minute: deep water goes shallow, what
   *  lies on the bed can be walked to, fire can cross what was wet. Gating-
   *  safe by construction: tideKind never changes what a bare hand crosses. */
  canEbb(game) {
    if (!game.state?.hasTide || !game.player) return false;
    if (this.tideCall && this.ticks < this.tideCall.until) return false;
    const [px, py] = this.tileOf(game.player);
    for (let y = py - EBB_REACH; y <= py + EBB_REACH; y++) for (let x = px - EBB_REACH; x <= px + EBB_REACH; x++) if (game.tileAt(x, y) === T.WATER) return true;
    return false;
  }
  callEbb(game) {
    const [x, y] = this.tileOf(game.player);
    this.tideCall = { until: this.ticks + Math.round(EBB_FRAMES * (wearing(game.state, 'pearl') ? 1.5 : 1)), tide: -1, x, y };   // the silt pearl (LS-302), which is itself down there
    this.applyTide(game, true);
  }
  /** The ebb the lung called, if it is still running and the body is by the basin it was called on. */
  calledHere(game) {
    if (!(this.tideCall && this.ticks < this.tideCall.until) || !game.player) return false;
    const [x, y] = this.tileOf(game.player);
    return Math.abs(x - this.tideCall.x) <= EBB_REACH && Math.abs(y - this.tideCall.y) <= EBB_REACH;
  }
  tideNow(game) {
    if (this.calledHere(game)) return this.tideCall.tide;
    return this.clock(game).tide;
  }

  /** WATER IS A VOLUME (LS-241). The tide, the rain, a fire's heat and the ebb charm all move one number per tile --
   *  how much water stands there -- and the tile's KIND is read off that number: deep over DEEP_LINE, shallow over
   *  WET_LINE, and the bed the basin was cut into when it is dry. `baseWet` remembers what the world made there, so
   *  only a tile the world made wet can ever hold water and no path the generator proved is closed by weather. */
  applyTide(game, first = false) {
    const tide = this.tideNow(game);
    const f = this.field, win = this.window(game), fresh: Rect[] = [];
    /* what the WORLD made there, read once a chunk, the first time the window reaches it (the robustness lens, round 48:
     * rebuilt from the live tiles on every entry, a screen walked onto at high water called its flooded shallows deep) */
    for (let sy = Math.floor(win.y0 / ROWS); sy * ROWS < win.y1; sy++) for (let sx = Math.floor(win.x0 / COLS); sx * COLS < win.x1; sx++) {
      const ck = sx + ',' + sy; if (this.wetSeen.has(ck) || sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) continue;
      this.wetSeen.add(ck); fresh.push({ x0: sx * COLS, y0: sy * ROWS, x1: (sx + 1) * COLS, y1: (sy + 1) * ROWS });
      const t = game.screenAt(sx, sy).tiles;
      for (let ly = 0; ly < ROWS; ly++) for (let lx = 0; lx < COLS; lx++) {
        const v = t[ly][lx]; if (v !== T.WATER && v !== T.SHALLOW && v !== T.FORD) continue;
        const k = (sx * COLS + lx) + ',' + (sy * ROWS + ly);
        this.baseWet.set(k, v); this.dryBed.set(k, v === T.FORD ? T.FORD : T.SAND);   // what a drained basin leaves: wet sand -- and a ford is itself again
      }
    }
    const baseAt = (x, y) => { const v = this.baseWet.get(x + ',' + y); return v === T.WATER ? DEPTH_DEEP : v === T.SHALLOW ? DEPTH_SHALLOW : v === T.FORD ? DEPTH_FORD : 0; };   // a ford sits between the lines: bare at low water, drowned at high (LS-383)
    f.depthBase = baseAt;
    /* THE NATURAL TIDE NEVER BARES A BED (the robustness lens, round 48): only the EBB CHARM takes the water below a
     * wadeable shallow, which is what the charm is for. */
    const called = this.calledHere(game);
    { const md = moodOfSeed(game.seedNum), floor = called || md.bare ? 0 : WET_LINE + 0.03;
      f.tickDepth(baseAt, tide, this.rain(game), this.fires || [], first ? 1 : 1 / 12, floor, md.tide, win);   // a tidal world's ebb bares the beds itself (2026-09-27)
      /* ground the window reaches for the first time is at the tide's level at once, as entering a screen used to set it (LS-511) */
      if (!first) for (const r of fresh) f.tickDepth(baseAt, tide, this.rain(game), this.fires || [], 1, floor, md.tide, r); }
    const p = game.player, [ptx, pty] = p ? this.tileOf(p) : [-1, -1];
    for (let y = win.y0; y < win.y1; y++) for (let x = win.x0; x < win.x1; x++) {
      const k = x + ',' + y;
      if (!this.baseWet.has(k) || this.marks.has(k)) continue;   // tiles the player changed: a boulder pushed into the water raised the bed, and the tide must not rewrite it back (2026-09-17)
      const d = f.depthAt(x, y);
      /* ICE (LS-381, the second element): a pool whose ground is under the frost line goes hard, and hard water is
       * ground -- anything on foot crosses it, the eel is shut beneath it, the tide cannot move it. */
      const cold = d >= WET_LINE && f.tempAt(x, y) < FROST_LINE;
      const c = game.chunkOf(x, y); if (!c) continue;
      const now = c.tiles[c.ly][c.lx];
      // ice under a body that could not stand in the water thaws on a later tick, once the body has moved (2026-09-19)
      if (!cold && now === T.ICE && p && !game.state.hasTide && ptx === x && pty === y) continue;
      const want = cold ? T.ICE : d >= DEEP_LINE ? T.WATER : d >= WET_LINE ? T.SHALLOW : (this.dryBed.get(k) ?? T.SAND);
      if (now !== want) { c.tiles[c.ly][c.lx] = want; if ((want === T.ICE || now === T.ICE) && game.markPartsDirty) game.markPartsDirty(c.sx, c.sy); }   // no rebake for water, which is drawn live; ice is a tile and wants one
    }
    this.lastTide = tide;
  }

  /** Lights, in world tiles: the lit braziers round the body, the wisps and a body carrying fire. */
  lights(game): { x: number; y: number }[] {
    const out = this.braziers(game, this.window(game));
    // A WISP IS A LIGHT (LS-242): it is the one creature made of light, so whatever comes to a brazier comes to it
    for (const e of game.enemies || []) if (!e.dead && (e.type || e.kind) === 'wisp' && e.spawnFx <= 0) { const [x, y] = this.tileOf(e); out.push({ x, y }); }
    // ...and a player carrying fire is a light too (LS-176): at night the wisps come to you
    const p = game.player;
    if (p && p.lit > 0) { const [x, y] = this.tileOf(p); out.push({ x, y }); }
    return out;
  }

  /** FIRE IS CARRIED (LS-176). Standing in fire lights the player for ten
   *  seconds: what they touch that can burn, catches (an ember gate's thorn
   *  stays locked: `ignite` reads the lock), a pod placed goes off in the
   *  hand, and water puts them out. The urge table already knows a light. */
  carryFire(game) {
    const p = game.player;
    if (!p) return;
    const [tx, ty] = this.tileOf(p);
    if (this.burning(tx, ty) && !p.submerged) p.lit = Math.max(p.lit || 0, LIT_FRAMES);
    if (!(p.lit > 0)) return;
    /* THE EMBER CHARM (LS-335): one frame in three costs the torch nothing, so it burns half again as long. Counted on
     * the player and not on `ticks` -- measured, keying the skip to ticks % 3 made a three-hundred-frame torch last
     * 1,409, because this is not called once a tick. */
    p.emberSkip = ((p.emberSkip || 0) + 1) % 3;
    if (!(game.state && wearing(game.state, 'embercharm') && p.emberSkip === 0)) p.lit--;
    /* THE OILSKIN KEEPS THE RAIN OFF THE TORCH TOO (2026-09-17). Its own words are 'rain runs off you: it never soaks,
     * and never puts your torch out', and the second half was never written: a downpour zeroed the torch whatever was
     * worn. Water still does -- wading, swimming, under -- and so does a soaking already taken. */
    const shed = !!(game.state && (wearing(game.state, 'oilskin') || (game.state.dry || 0) > game.runFrames));
    if (p.wading || p.swimming || p.submerged || (this.rain(game) > 0.6 && !shed) || afflicted(p, 'Soaked')) { p.lit = 0; game.puff(p.cx, p.cy - 4, UI.underPale, 4, 'splash'); return; }   // and a downpour puts the torch out (LS-223)
    if (this.ticks % 5 === 0) game.puff(p.cx + (((this.ticks / 5) & 1) ? 3 : -3), p.y - 2, (this.ticks % 10) ? UI.gold : UI.flame, 3, 'ember');
    if (this.ticks % 10 === 0) {
      const [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir] || [0, 0];
      const ax = Math.floor((p.cx + dx * (p.w / 2 + 4)) / TS), ay = Math.floor((p.cy + dy * (p.h / 2 + 4)) / TS);
      this.ignite(game, ax, ay);
    }
  }

  /** The pull a creature feels this frame, in px. */
  urgeFor(game, e): [number, number] {
    if (!this.fires.length && !(this.clock(game).night)) return [0, 0];
    const tx = e.cx / TS, ty = e.cy / TS;
    // THROUGH THE THINGS (LS-222): the body's own parts (Fears, DrawnToLight, from its traits) answer 'urge'
    const tr = traitsOf(e.type || e.kind);
    const bodyParts: any[] = []; if (tr.fearsFire) bodyParts.push(Fears(tr.fearsFire)); if (tr.drawnToLight) bodyParts.push(DrawnToLight(tr.drawnToLight)); if (tr.eatsFire) bodyParts.push(EatsFire(tr.eatsFire));   // the cinder's own pull, which reached nothing before (2026-09-18)
    const [ux, uy] = urgeOf(this.simWorld(game), thing(-2, tx, ty, bodyParts), this.fires, this.lights(game), this.clock(game).night);
    return [ux * 1.2, uy * 1.2];
  }

  /** Seed pods ripen on the clock: with the Rattle held, a pouch below five
   *  gains one every two game-minutes. Dying costs a pod (game.ts), and a
   *  pouch that could only empty left a player at a pod gate with nothing --
   *  the finisher's run-2 died twelve times in a duel and stood at rubble
   *  for the rest of its run. The charm grants the capability; the clock
   *  keeps it. Nothing a bare hand could not already do is opened by it. */
  ripen(game) {
    const st = game.state;
    if (!st || !(st.rattle || st.hasPods)) return;   // the Rattle's own clock (2026-09-18): keyed to the bag's count, the ripen rule stopped exactly when the pouch was empty
    const m = this.clock(game).minutes;
    if (st.pods >= 5) { this.lastRipen = m; return; }
    if (this.lastRipen === undefined) this.lastRipen = m;
    if (m - this.lastRipen >= RIPEN_MINUTES) {
      if (addItem(st, 'pod', 1) === 0) this.lastRipen = m;   // the pod goes in the BAG (2026-09-17): `state.pods` is a mirror of the bag that syncFlags derives, so incrementing it made a count no stack backed -- the HUD showed a pod the hand could not find
    }
  }

  windBase = 0;
  /** THE WIND VEERS ROUND THE COMPASS ONCE A DAY (LS-383). One fixed wind a world meant a raft, which only the wind
   *  moves, could reach only the islands downwind of the glade and never come back; now a crossing is planned by
   *  the hour, and a raft adrift meets some shore before the day is out. The spores and the smoke turn with it. */
  /** THE SEA SHOWS THE WIND (LS-389, 2026-09-12): a raft only drifts, so a player on the shore has to read the wind
   *  before launching, and smoke and spores are not always in sight. Foam streaks run downwind over open sea: every
   *  five frames one streak is laid on a sea tile of this screen and flies with the wind, straighter and longer than
   *  the air's own streaks, so from any beach the wind's direction is in the water. */
  seaStreaks(game) {
    if (!this.wind || !game.wind) return;
    const cam = game.camera ? game.camera() : { x: game.originX(), y: game.originY() };
    const x0 = Math.floor(cam.x / TS), y0 = Math.floor(cam.y / TS);
    const sea: [number, number][] = [];
    for (let ty = y0; ty < y0 + ROWS + 1; ty++) for (let tx = x0; tx < x0 + COLS + 1; tx++) if (game.tileAt(tx, ty) === T.SEA) sea.push([tx, ty]);
    if (!sea.length) return;
    // a tile with sea two tiles downwind too, so the streak stays on the water for its whole life
    // ...and one that leaves the water dies there: a streak is foam, and foam does not cross a beach
    for (const p of game.fx || []) if (p.foam && p.life > 0 && game.tileKindAt(p.x, p.y) !== T.SEA) p.life = 0;
    const wx = Math.round(this.wind[0] * 1.5), wy = Math.round(this.wind[1] * 1.5);
    const open = sea.filter(([tx, ty]) => game.tileAt(tx + wx, ty + wy) === T.SEA);
    if (!open.length) return;
    const [tx, ty] = open[Math.floor(Math.random() * open.length)];
    const x = tx * TS + Math.random() * TS, y = ty * TS + Math.random() * TS;
    game.wind(x, y, this.wind[0], this.wind[1], 1, 0.08, 0.35, { span: 16, curl: 0.01, drag: 0.995, bias: 0, life: 48 + Math.floor(Math.random() * 24), foam: true });
  }
  update(game) {
    this.ticks++;
    { const a = this.windBase + this.clock(game).day * Math.PI * 2; this.wind = [Math.cos(a), Math.sin(a)]; }
    if (game.state && game.state.gust && game.state.gust.until > game.runFrames) this.wind = game.state.gust.dir;   // a windfall holds the wind toward the beast for twenty seconds (LS-335)
    if (this.ticks % 5 === 0 && game.area && game.tiles && game.mode === 'play') this.seaStreaks(game);   // the wind, read off the sea (LS-389)
    if (game.area && game.tiles) this.wearAndWeather(game);
    if (game.area && game.tiles && game.mode === 'play') this.storm(game);   // the sky charges in a downpour and strikes (LS-381)
    if (this.ticks % 20 === 0 && game.area && game.tiles && game.mode === 'play') this.forge(game);   // the fire takes a whole recipe (LS-384)
    this.tickPack(game);   // ...and it lives somewhere when it is not here (LS-380)
    this.prowl(game);   // the night comes for you (LS-379)
    if (this.ticks % 30 === 0) this.hourPasses(game);   // ...and what the hour no longer admits leaves (2026-09-19)
    if (this.ticks % 60 === 0) this.ripen(game);
    this.carryFire(game);
    /* Past the ebb's deadline the call is retired and the water comes back at once (2026-09-19). */
    if (this.tideCall && this.ticks >= this.tideCall.until) { this.tideCall = null; this.applyTide(game, true); }
    else if (this.ticks % TIDE_TICK === 0 || !this.windowSeen(game)) this.applyTide(game);   // ...and at once when the window reaches ground it has not read
    if (this.ticks % 60 === 0) this.regrowSweep(game);   // cut grass and scorch grow back where their time has come (LS-511: replayed on entering a screen)
    if (this.ticks % FRAMES_PER_MINUTE === 0) this.nestSweep(game);
    if (this.ticks % FRAMES_PER_MINUTE === FRAMES_PER_MINUTE / 2) this.thornSpread(game);   // and the thickets creep (LS-525)   // and the nests round the body spread
    if (this.ticks % FIRE_TICK === 0 && game.area && game.tiles) {
      // THE RAIN (LS-223): every fire tick the wet field takes the rain, a tile under a tree a fifth of it
      const grid0 = this.grid(game), win = this.window(game);
      const rf = this.field;
      rf.tickRain(this.rain(game), (x, y) => grid0.at(x, y) === T.TREE, win);
      /* AND THE STANDING WETNESS OF THE GROUND, which was computed ONLY inside a fire tick (things.ts) -- so on
       * every screen with nothing burning it read zero: the shore's cooling in tickTemp was dead, dryness was
       * wrong, and a mossback, which drinks from it, dried out and died beside a pool (2026-09-17). */
      rf.wetFromGrid(grid0, win);
      rf.tickedAt = game.runFrames;
      // ...and when the screen's wet step changes, it is painted again in its wet state (2026-09-11)
      if (game.wetQuantumOf) { const q = game.wetQuantumOf(game.area.x, game.area.y); if (q !== this.lastWetQ) { this.lastWetQ = q; if (game.markPartsDirty) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) game.markPartsDirty(game.area.x + dx, game.area.y + dy); } }   // the neighbours too: they stand in the same rain   // under a canopy the ground takes a fifth of it; a hedge in the open is rained on like the rest
    }
    if (this.ticks % FIRE_TICK !== 0 || !this.fires.length) { this.burnBodies(game); return; }
    this.fireStep(game);
    this.burnBodies(game);
  }

  /** THE PACK LIVES ON SCREENS YOU ARE NOT ON (LS-380, the Rain World piece;
   *  2026-09-11). The night's pack was conjured at the edge of the glass every
   *  seven seconds and vanished at dawn: a hazard timer in a costume, true of
   *  nothing when you were not looking. It is a thing in the world now -- a
   *  den, a size, a hunger, and where it is -- stepped once a minute wherever
   *  you are, in the pattern the fires use. It sleeps in the den by day. At
   *  dusk it wakes and moves one screen a minute toward the nearer of your
   *  scent (the screen you are on) and prey (a screen whose roster holds a
   *  living creature within PACK_RANGE). On a screen with prey and nobody
   *  watching it hunts: the roster loses one creature a minute, the pack is
   *  fed, and the screen gets a kill mark -- bones, laid into the placed
   *  layer the way the beasts' marks are and capped like theirs. A pack that
   *  ate well is bigger tomorrow (one more for every two eaten, to PACK_MAX);
   *  one cut down on the glass is smaller. At dawn it goes home by the
   *  shortest road and sleeps. Legible without a word: bones on a screen say
   *  it was here, missing creatures say what it ate, the side it comes in by
   *  says where it lives, and a den found by day is a place to burn out,
   *  hedge in, or leave alone. */
  packFor(game) {
    if (this.pack || !game.world || !game.world.biome) return this.pack;
    const H = game.world.biome.length, W = game.world.biome[0].length;
    const start = game.world.start || { x: 0, y: 0 };
    const rng = new RNG(mix(game.seedNum, 380, 3));
    const far: string[] = [];
    const land = (sx: number, sy: number) => !game.world.island || game.world.island[sy * W + sx] >= 0;   // a den is dug on land (LS-383): a pack denned on open sea had no ground to scorch and never lost its den
    /* ...AND NEVER IN A BEAST'S LAIR OR THE SCREENS ROUND IT (2026-09-12; beasts.ts fell-2): the den fell on the oldshell's
     * screen and the pack's chargers fought beside the beast, and the body died to them while the beast stood idle.
     * A beast fight is the beast's. */
    const lairs = game.beastLairs ? (game.beastLairs() || []) : [];
    const nearLair = (sx: number, sy: number) => lairs.some((l: any) => Math.max(Math.abs(sx - l.x), Math.abs(sy - l.y)) <= 1);
    for (let sy = 0; sy < H; sy++) for (let sx = 0; sx < W; sx++) if (land(sx, sy) && !nearLair(sx, sy) && Math.max(Math.abs(sx - start.x), Math.abs(sy - start.y)) >= DEN_AWAY) far.push(sx + ',' + sy);
    /* ...AND ON THE GLADE'S OWN ISLAND WHEN IT HAS ONE (2026-09-19): on the archipelago most far screens are other
     * islands, and a pack denned across the water walked to you over open sea. Its road stays on land now (`step`),
     * so a den it cannot walk from is a pack that never comes; the home island is preferred while it has room. */
    const home = far.filter((k) => { const [sx, sy] = k.split(',').map(Number); return this.sameIsle(game, sx, sy, start.x, start.y); });
    if (home.length) far.splice(0, far.length, ...home);
    if (!far.length) return null;
    const den = far[rng.int(far.length)];
    this.pack = { den, at: den, size: PACK_SIZE, fed: 0, awake: false, from: [0, 0], lost: 0 };   // lost: dens burned out from under it
    return this.pack;
  }
  /** The den burned out: another far screen, chosen by seed and the count of dens lost; the pack halved, never below one. */
  moveDen(game, pack) {
    const H = game.world.biome.length, W = game.world.biome[0].length;
    const start = game.world.start || { x: 0, y: 0 };
    const far: string[] = [];
    const land = (sx: number, sy: number) => !game.world.island || game.world.island[sy * W + sx] >= 0;   // a den is dug on land (LS-383)
    // never within a screen of a living beast's lair, the rule the first den keeps (2026-09-19): a moved den fell on the oldshell's screen
    const lairs = game.beastLairs ? (game.beastLairs() || []) : [];
    const nearLair = (sx: number, sy: number) => lairs.some((l: any) => Math.max(Math.abs(sx - l.x), Math.abs(sy - l.y)) <= 1 && !(game.state.beastsSlain || []).includes(l.kind));
    for (let sy = 0; sy < H; sy++) for (let sx = 0; sx < W; sx++) if (land(sx, sy) && !nearLair(sx, sy) && Math.max(Math.abs(sx - start.x), Math.abs(sy - start.y)) >= DEN_AWAY && sx + ',' + sy !== pack.den) far.push(sx + ',' + sy);
    const home = far.filter((k) => { const [sx, sy] = k.split(',').map(Number); return this.sameIsle(game, sx, sy, start.x, start.y); });
    if (home.length) far.splice(0, far.length, ...home);
    if (!far.length) return;
    pack.lost = (pack.lost || 0) + 1;
    const rng = new RNG(mix(game.seedNum, 380, 7 + pack.lost));
    pack.den = far[rng.int(far.length)]; pack.at = pack.den;
    pack.size = Math.max(1, Math.floor(pack.size / 2));
  }
  /** Two screens on the same island (a world with no islands is one island). */
  sameIsle(game, ax: number, ay: number, bx: number, by: number) {
    const isle = game.world.island; if (!isle) return true;
    const W = game.world.biome[0].length;
    return isle[ay * W + ax] === isle[by * W + bx];
  }
  /** The pack, if it is awake on the player's screen. */
  packHere(game) {
    const pack = this.pack;
    return pack && pack.awake && pack.at === game.area.x + ',' + game.area.y ? pack : null;
  }
  /** The prey the pack can smell: a roster with a living creature that is not a beast, within range. */
  preyScreens(game, at: [number, number]) {
    const out: [number, number, number][] = [];
    for (const [key, list] of game.bodiesByChunk ? game.bodiesByChunk() : []) {   // every body, awake or asleep, by the chunk it stands in (LS-511)
      if (!list.some((e: any) => e && !e.dead && !(e.def && e.def.boss))) continue;
      const [sx, sy] = key.split(',').map(Number);
      const d = Math.max(Math.abs(sx - at[0]), Math.abs(sy - at[1]));
      if (d <= PACK_RANGE) out.push([sx, sy, d]);
    }
    return out;
  }
  tickPack(game) {
    if ((globalThis as any).__noPack || !game.world || !game.area) return;
    const minute = Math.floor((game.runFrames || 0) / FRAMES_PER_MINUTE);
    if (minute === this.packMinute) return;
    this.packMinute = minute;
    const pack = this.packFor(game);
    if (!pack) return;
    const night = this.clock(game).night;
    const at = pack.at.split(',').map(Number) as [number, number];
    const step = (to: [number, number]) => {
      const dx = Math.sign(to[0] - at[0]), dy = Math.sign(to[1] - at[1]);
      if (!dx && !dy) return false;
      // the larger leg first, so the road is the shortest one
      const goX = Math.abs(to[0] - at[0]) >= Math.abs(to[1] - at[1]);
      /* ...AND NEVER OVER THE SEA (2026-09-19): the road took the larger leg whatever was there, and on the
       * archipelago that was open water. A leg onto a sea screen is refused, the other leg tried, and a pack with
       * the sea on both sides waits on its shore this minute. Waiting is not arriving: `step` still says it moved. */
      const W = game.world.biome[0].length;
      const onLand = (mx: number, my: number) => { const isle = game.world.island; return !isle || isle[(at[1] + my) * W + at[0] + mx] >= 0; };
      const legs = (goX ? [[dx, 0], [0, dy]] : [[0, dy], [dx, 0]]).filter(([mx, my]) => (mx || my) && onLand(mx, my));
      if (!legs.length) { pack.from = [0, 0]; return true; }
      pack.from = legs[0] as [number, number];
      pack.at = (at[0] + pack.from[0]) + ',' + (at[1] + pack.from[1]);
      return true;
    };
    if (!night) {
      if (!pack.awake) return;
      const den = pack.den.split(',').map(Number) as [number, number];
      if (!step(den)) {
        /* A DEN BURNED OUT (LS-380's den, 2026-09-11). A den you find by day is
         * a place you can choose to burn: if the pack comes home at dawn to a
         * screen with DEN_BURNT scorched tiles, it does not sleep there. It moves
         * on to another far screen, halved, and the next nights are what a
         * smaller pack from a farther den can bring. The record keeps the den
         * it lost, so the marks it left mean what they meant. */
        const tiles = game.screenAt(den[0], den[1]).tiles;
        let scorched = 0; for (const row of tiles) for (const v of row) if (v === T.SCORCH) scorched++;
        if (scorched >= DEN_BURNT) { this.moveDen(game, pack); }
        pack.awake = false; pack.from = [0, 0];
      }
      return;
    }
    if (!pack.awake) { pack.awake = true; pack.at = pack.den; pack.from = [0, 0]; return; }
    const here: [number, number] = [game.area.x, game.area.y];
    const onPlayer = at[0] === here[0] && at[1] === here[1];
    // prey under its feet, and nobody watching: eat
    if (!onPlayer) {
      const list = game.bodiesByChunk ? game.bodiesByChunk().get(pack.at) : null;
      const prey = list && list.find((e: any) => e && !e.dead && !(e.def && e.def.boss) && !(game.inWindow && game.inWindow(e, TS * 2)));   // nobody watching
      if (prey) {
        prey.dead = true;
        pack.fed++;
        if (pack.fed % 2 === 0) pack.size = Math.min(PACK_MAX, pack.size + 1);   // every two eaten make it one bigger FROM WHERE IT IS (2026-09-19): the lifetime count restored a pack cut to two by the player's kills to five at one meal
        this.killMark(game, at, minute);
        return;
      }
    }
    // toward the nearer of the player's scent and the nearest prey; the player on a tie
    const dPlayer = Math.max(Math.abs(here[0] - at[0]), Math.abs(here[1] - at[1]));
    let target: [number, number] = here, best = dPlayer;
    for (const [sx, sy, d] of this.preyScreens(game, at)) if (d < best) { best = d; target = [sx, sy]; }
    step(target);
  }
  /** Bones where the pack ate: a bones tile on open ground, into the placed layer, capped like the beasts' marks. */
  killMark(game, at: [number, number], minute: number) {
    const key = 'W|' + at[0] + ',' + at[1];
    const marks = game.placed.get(key);
    if (marks && [...marks.values()].filter((k) => k !== T.ROCK).length >= MARK_CAP) return;
    const tiles = game.screenAt(at[0], at[1]).tiles;
    const rng = new RNG(mix(game.seedNum, minute, at[0] * 7 + at[1], 380));
    const spots: [number, number][] = [];
    /* ...AND NOT UNDER A CROWN: two of three marks laid on a meadow sat under a tree's canopy and could not be seen
     * at all (round 53's art lens). A spot beside a tree is passed over while any other will do. */
    const clear = (tx: number, ty: number) => { for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const r = tiles[ty + dy]; if (r && r[tx + dx] === T.TREE) return false; } return true; };
    const open: [number, number][] = [];
    /* ...AND NOT ON THE WATERLINE (2026-09-13): sand beside the sea is under the sea's own band in the picture, and a mark
     * left there could not be seen at all (round53.ts, play-1's 14,6). Sand a tile back from the water will do. */
    const wetBeside = (tx: number, ty: number) => [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => { const r = tiles[ty + dy]; const v = r && r[tx + dx]; return v === T.SEA || v === T.WATER || v === T.SHALLOW; });
    for (let ty = 1; ty < ROWS - 1; ty++) for (let tx = 1; tx < COLS - 1; tx++) { const v = tiles[ty][tx]; if (v === T.GROUND || v === T.MOSS || v === T.TUFT || (v === T.SAND && !wetBeside(tx, ty))) { open.push([tx, ty]); if (clear(tx, ty)) spots.push([tx, ty]); } }
    if (!spots.length) spots.push(...open);
    if (!spots.length) return;
    const [tx, ty] = spots[rng.int(spots.length)];
    tiles[ty][tx] = T.BONES;   // the tile itself, not only the record: a screen already made shows it now, not after a reload (2026-09-11)
    if (!game.placed.has(key)) game.placed.set(key, new Map());
    game.placed.get(key).set(tx + ',' + ty, T.BONES);
    if (game.markPartsDirty) game.markPartsDirty(at[0], at[1]);   // and the turf over it is painted again, or the meadow grows over the bones (round 53's art lens)
    game.partsCache.delete(at[0] + ',' + at[1]);
  }

  /** One step of the fire rule: what catches, what is spent, and the scorch it leaves. */
  fireStep(game) {
    const grid = this.grid(game);
    const { day, minutes } = this.clock(game);
    // THE FIRE BURNS AS THINGS (LS-222): the same seeded rolls as the rule in matter.ts, made by parts answering
    // events on the bus; the soak's 'fire as parts' section runs both over random worlds and asserts they agree.
    // EVERY FIRE IN THE WORLD, wherever it is (LS-511): a thicket lit and walked away from burns on, not replayed.
    const { caught, spent } = (globalThis as any).__fireRule
      ? tickFire(grid, this.fires, game.seedNum, this.ticks, this.biomeDry(game), day, this.wind)
      : tickFireThings(grid, this.fires, game.seedNum, this.ticks, this.biomeDry(game), day, this.wind, (() => { const w = new SimWorld(grid, game.seedNum); w.fields = this.field; w.window = this.window(game); return w; })());
    for (const f of caught) {
      // a fire that reaches a bowl LIGHTS it (2026-09-19): it used to be pushed as a fire on the brazier's tile, which the tile never answered
      if (f.kind === T.BRAZIER) { if (game.lightBrazierAt) game.lightBrazierAt(f.x, f.y, true); continue; }
      if (this.fires.length >= FIRE_CAP) break;
      this.fires.push(f);
      this.flameFor(game, f.x, f.y);
    }
    if (spent.length) {
      for (const f of spent) {
        // a thorn the fire took is broken the way a struck one is: the hollow it sealed opens and the break is saved
        if ((f as any).doused) { game.puff && game.puff(f.x * TS + 8, f.y * TS + 4, UI.underPale, 4, 'splash'); continue; }   // put out by the rain: a wisp of steam, the tile as it was (LS-223)
        if (f.kind === T.THORN && game.markBurntAt) game.markBurntAt(f.x, f.y);
        if (grid.at(f.x, f.y) === T.SCORCH) this.scorched.set(f.x + ',' + f.y, minutes);
      }
      this.fires = this.fires.filter((f) => !spent.includes(f));
    }
    // a pod in the fire is a pod that goes off
    for (const pod of game.pods || []) {
      if (pod.dead || pod.fuse <= 1) continue;   // the fuse counts down from 95; 1 is now
      if (this.burning(Math.floor(pod.cx / TS), Math.floor(pod.cy / TS))) pod.fuse = 1;
    }
    this.burnBodies(game);
  }

  /** Bodies standing in fire are hurt, if fire hurts them. */
  burnBodies(game) {
    if (!this.fires.length || this.ticks % BURN_EVERY !== 0) return;
    /* THROUGH THE THINGS (LS-222): every body standing in a fire is told so, and its parts answer -- Burns hurts
     * (and sets a bramble alight), Fuse lights a pod's fuse, a wisp has neither and feels nothing. */
    const w = new SimWorld(this.grid(game), game.seedNum); w.tick = this.ticks;
    w.fires = this.fires.map((f) => ({ id: 0, kind: -1, x: f.x, y: f.y, parts: new Map() } as any));
    standInFire(w, bodyThings(game, traitsOf, 0, 0, TS), { game, tick: this.ticks, torchFrames: TORCH_FRAMES });
  }


  /** WHAT IS BURNING IS SAVED (2026-09-18), and every ledger with it -- in world tiles since LS-511 (`v: 2`). A save
   *  from before filed each under a screen key ('W|sx,sy') with the screen's own tile numbers; `restore` reads both. */
  serialize(_game?: any) {
    const flat = (m: Map<string, number>, cap: number) => [...m].slice(0, cap);
    return { v: 2, pack: this.pack ? { ...this.pack, from: [...this.pack.from] } : null, scorched: flat(this.scorched, 4096), tufts: flat(this.tufts, 4096), nests: flat(this.nests, 1024), marks: flat(this.marks, 4096), lastRipen: this.lastRipen ?? null,
      fires: this.fires.slice(0, FIRE_CAP).map((f: any) => [f.x, f.y, f.fuel, f.kind ?? -1, f.struck ? 1 : 0]) };
  }

  restore(data) {
    this.scorched = new Map(); this.tufts = new Map(); this.nests = new Map(); this.marks = new Map();
    this.fires = [];
    this.pack = null; this.packMinute = -1;
    if (!data) return;
    /* THE PACK IS READ AS THE PLACED LAYER IS: two screen keys, a size in range, a hunger, a flag and a direction;
     * anything else is no pack. (The pack walks the world a chunk a minute: its road is coarse on purpose.) */
    const pk = data.pack;
    const keyOk = (k: any) => typeof k === 'string' && /^\d{1,2},\d{1,2}$/.test(k);
    if (pk && keyOk(pk.den) && keyOk(pk.at) && typeof pk.size === 'number' && pk.size >= 1 && pk.size <= PACK_MAX && typeof pk.fed === 'number' && Array.isArray(pk.from) && pk.from.length === 2) {
      this.pack = { den: pk.den, at: pk.at, size: Math.floor(pk.size), fed: Math.max(0, Math.floor(pk.fed)), awake: !!pk.awake, from: [Math.sign(pk.from[0] | 0), Math.sign(pk.from[1] | 0)], lost: Math.max(0, Math.floor(pk.lost || 0)) };
    }
    const WW = WORLD_W * COLS, WH = WORLD_H * ROWS;
    const inWorld = (x: number, y: number) => Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < WW && y < WH;
    /* a ledger is either flat [['x,y', n], ...] in world tiles (v2) or, from before LS-511, [['W|sx,sy', [['x,y', n], ...]], ...] */
    const read = (rows: any, into: Map<string, number>, cap: number, numeric: boolean) => {
      if (!Array.isArray(rows)) return;
      const put = (x: number, y: number, n: any) => { if (into.size < cap && inWorld(x, y) && (!numeric || (typeof n === 'number' && Number.isFinite(n)))) into.set(x + ',' + y, n); };
      for (const row of rows.slice(0, 4096)) {
        if (!Array.isArray(row) || typeof row[0] !== 'string') continue;
        const old = /^W\|(\d{1,2}),(\d{1,2})$/.exec(row[0]);
        if (old && Array.isArray(row[1])) { const sx = +old[1], sy = +old[2]; for (const e of row[1].slice(0, 256)) { if (!Array.isArray(e) || typeof e[0] !== 'string') continue; const [lx, ly] = e[0].split(',').map(Number); if (lx >= 0 && ly >= 0 && lx < COLS && ly < ROWS) put(sx * COLS + lx, sy * ROWS + ly, e[1]); } }
        else if (/^\d{1,3},\d{1,3}$/.test(row[0])) { const [x, y] = row[0].split(',').map(Number); put(x, y, row[1]); }
      }
    };
    read(data.marks, this.marks, 4096, true);
    read(data.nests, this.nests, 1024, true);
    read(data.tufts, this.tufts, 4096, true);
    read(data.scorched, this.scorched, 4096, true);
    this.lastRipen = typeof data.lastRipen === 'number' ? data.lastRipen : undefined;
    /* THE FIRES: flat [x, y, fuel, kind, struck] in world tiles (v2), or from before, [screen key, minute, [[x, y, ...]]] */
    const fire = (x: number, y: number, f: any[]) => { if (this.fires.length < FIRE_CAP && inWorld(x, y) && typeof f[2] === 'number' && Number.isFinite(f[2]) && f[2] > 0 && !this.burning(x, y)) this.fires.push({ x, y, fuel: Math.min(600, Math.floor(f[2])), born: 0, kind: typeof f[3] === 'number' ? f[3] : -1, struck: !!f[4] } as any); };
    if (Array.isArray(data.fires)) for (const row of data.fires.slice(0, 512)) {
      if (!Array.isArray(row)) continue;
      const old = typeof row[0] === 'string' ? /^W\|(\d{1,2}),(\d{1,2})$/.exec(row[0]) : null;
      if (old && Array.isArray(row[2])) { const sx = +old[1], sy = +old[2]; for (const f of row[2].slice(0, 64)) if (Array.isArray(f) && f.length >= 3 && f[0] >= 0 && f[0] < COLS && f[1] >= 0 && f[1] < ROWS) fire(sx * COLS + Math.floor(f[0]), sy * ROWS + Math.floor(f[1]), f); }
      else if (typeof row[0] === 'number' && typeof row[1] === 'number') fire(Math.floor(row[0]), Math.floor(row[1]), row);
    }
  }
}
